import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Alert, Spinner, Button, Row, Col, Card } from 'react-bootstrap';
import useUser from '../../hooks/useUser';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FaCheckCircle, FaTimes, FaCalendarAlt, FaPhone, FaUsers, FaShieldAlt, FaRocket, FaBrain, FaChartLine, FaGraduationCap, FaRobot, FaBook, FaStar, FaArrowRight } from 'react-icons/fa';

// Components
import InfoModal from '../../components/InfoModal';
import { usePaymentStatus } from '../../contexts/PaymentStatusContext';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';

// Analytics and data
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';

// Data
import planFeaturesData from '../../data/planFeatures.json';

// Styles
import './UpgradeToProBookCallPage.css';

/**
 * UPGRADE TO PRO BOOK CALL PAGE - A/B TEST VARIANT
 * 
 * This is the VARIANT page for the Upgrade Page Book Call A/B test.
 * 
 * KEY DIFFERENCES FROM CONTROL:
 * - Primary CTA: "Book Expert Call" instead of "Upgrade Now"
 * - Inline Calendly widget for seamless call booking
 * - Secondary CTA: "Upgrade Directly" for users who want to skip call
 * - Call-focused messaging and social proof
 * - Dual conversion tracking: call bookings + direct purchases
 * 
 * ANALYTICS STRATEGY:
 * - Primary metric: book_expert_call_clicked → BookCallClick (PostHog)
 * - Success metric: expert_call_booked → ExpertCallBooked (PostHog)
 * - Secondary metric: upgrade_to_pro_cta_clicked (for direct upgrade CTA)
 * - Completion metric: upgrade_to_pro_success (for direct purchases)
 */

function UpgradeToProBookCallPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Auth and payment status
  const { user, isAuthenticated, isLoading: authLoading } = useUser();
  const { isPayingCustomer, isLoading: paymentStatusLoading } = usePaymentStatus();
  
  // URL parameters
  const fromStarterSignup = searchParams.get('from_starter_signup') === 'true';
  const emailFromQuery = searchParams.get('email');
  const customerIdFromQuery = searchParams.get('customerId');
  
  // Component state
  const [showExitModal, setShowExitModal] = useState(false);
  const [showBookingSuccess, setShowBookingSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3);

  // Pricing state (same as other pages)
  const [pricingInfo, setPricingInfo] = useState(null);
  const [isLoadingPricing, setIsLoadingPricing] = useState(true);
  const [pricingError, setPricingError] = useState(null);
  
  const { startCheckout: startProCheckout } = useCheckoutRedirect('upgrade_to_pro_book_call');

  // Get initial form values from URL params
  const initialEmailForForm = searchParams.get('email') || user?.email || '';
  const initialNameForForm = searchParams.get('name') || user?.name || '';

  // Determine if "Stay on Starter" CTAs should be shown (only after starter signup)
  const showStayOnStarterCTAs = fromStarterSignup;

  const launchProCheckout = useCallback(
    (ctaLocation = 'book_call_secondary_cta') => {
      const contactEmail = initialEmailForForm || emailFromQuery || user?.email || '';
      const contactName = initialNameForForm || user?.name || '';

      return startProCheckout({
        products: [
          { productSlug: 'pro-plan-upfront' },
          { productSlug: 'pro-plan' },
        ],
        metadata: {
          origin: {
            flow: 'upgrade_to_pro_book_call',
            ctaLocation,
            sourcePath: location.pathname,
          },
        },
        contact: {
          email: contactEmail,
          name: contactName,
        },
      });
    },
    [emailFromQuery, initialEmailForForm, initialNameForForm, location.pathname, startProCheckout, user?.email, user?.name],
  );

  // Helper to format pricing for display (same as other pages)
  const addDollarSign = (str) => {
    if (!str || str === 'TBD') return str;
    const withDollar = str.startsWith('$') ? str : `$${str}`;
    return withDollar.replace('/month', '/mo');
  };

  // Get formatted pricing details (same as other pages)
  const getFormattedPricing = () => {
    if (!pricingInfo?.products) {
      return { upfront: 'TBD', monthly: 'TBD' };
    }
    
    const upfrontProduct = pricingInfo.products.find(p => p.slug === 'pro-plan-upfront');
    const monthlyProduct = pricingInfo.products.find(p => p.slug === 'pro-plan');
    
    const upfrontPrice = upfrontProduct?.discountedPrice?.formatted || upfrontProduct?.basePrice?.formatted || 'TBD';
    const monthlyPrice = monthlyProduct?.discountedPrice?.formatted || monthlyProduct?.basePrice?.formatted || 'TBD';
    
    return {
      upfront: addDollarSign(upfrontPrice),
      monthly: addDollarSign(monthlyPrice),
      upfrontOriginal: upfrontProduct?.basePrice?.formatted ? addDollarSign(upfrontProduct.basePrice.formatted) : null,
      monthlyOriginal: monthlyProduct?.basePrice?.formatted ? addDollarSign(monthlyProduct.basePrice.formatted) : null,
      isDiscounted: !!(upfrontProduct?.discountedPrice || monthlyProduct?.discountedPrice)
    };
  };

  // Fetch product pricing (same logic as other pages)
  const fetchProductPricing = useCallback(async () => {
    setIsLoadingPricing(true);
    setPricingError(null);
    
    const proProductSlugs = 'pro-plan,pro-plan-upfront';
    const API_URL = process.env.REACT_APP_API_URL || 'https://api.botspot.trade';
    const url = `${API_URL}/stripe/products-pricing?slugs=${proProductSlugs}`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Failed to fetch pricing details. Status: " + response.status }));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }
      const data = await response.json();
      setPricingInfo(data);
    } catch (error) {
      console.error("Failed to fetch product pricing:", error);
      setPricingError(error.message || "Could not load pricing. Please try again.");
      // Fallback pricing
      setPricingInfo({
        products: [
          { slug: 'pro-plan-upfront', basePrice: { formatted: 'TBD' }, name: 'Pro Plan Upfront Fee' },
          { slug: 'pro-plan', basePrice: { formatted: 'TBD' }, name: 'Pro Plan Monthly' }
        ],
        summary: { originalFormatted: 'Pricing unavailable' },
        error: "Could not load pricing information."
      });
    } finally {
      setIsLoadingPricing(false);
    }
  }, []);

  // Fetch pricing on mount
  useEffect(() => {
    fetchProductPricing();
  }, [fetchProductPricing]);

  // Exit intent tracking
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        setShowExitModal(true);
        pushEvent({ 
          event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_EXIT_INTENT_MODAL_VIEWED, 
          source: location.pathname,
          variant: 'book_call_page' 
        });
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [location.pathname]);

  // Track page view
  useEffect(() => {
    pushEvent({
      event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_PAGE_VIEW,
      source: location.pathname,
      variant: 'book_call_page',
      experiment_name: 'upgrade_page_book_call_test',
      experiment_variant: 'test',
      page_title: 'Upgrade to Pro - Book Call Variant',
      from_starter_signup: fromStarterSignup,
      email: initialEmailForForm,
      customerId: customerIdFromQuery
    });
  }, [location.pathname, fromStarterSignup, initialEmailForForm, customerIdFromQuery]);

  // 1. Load Calendly script once - no early returns that break listener
  useEffect(() => {
    console.log('🗓️ Loading Calendly script...');
    
    // Always check, but don't early return - just skip adding duplicate script
    if (!document.querySelector('script[data-calendly="true"]')) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.type = 'text/javascript';
      script.async = true;
      script.dataset.calendly = 'true'; // Mark to prevent duplicates
      
      script.onload = () => {
        console.log('🗓️ Calendly script loaded successfully!');
        console.log('🗓️ window.Calendly:', window.Calendly);
      };
      
      script.onerror = () => {
        console.error('🗓️ Failed to load Calendly script');
      };
      
      document.head.appendChild(script);
    } else {
      console.log('🗓️ Calendly script already exists');
    }
  }, []); // Run once on mount

  // 2. Always attach postMessage listener - NEVER early return
  useEffect(() => {
    console.log('🔧 [DEBUG] Adding Calendly postMessage event listener...');
    
    const handleCalendlyEvent = (e) => {
      // More robust event detection as suggested
      const isCalendlyEvent = 
        e.origin === 'https://calendly.com' && 
        typeof e.data?.event === 'string' && 
        e.data.event.startsWith('calendly.');

      if (!isCalendlyEvent) return;

      // 🚩 Now we will see these logs!
      console.log('🔍 [Calendly Event]', e.data.event, e.data.payload);
      
      if (e.data.event === 'calendly.event_scheduled') {
        console.log('🗓️ CALENDLY BOOKING COMPLETED!', e.data);
        
        // Track successful call booking
        pushEvent({
          event: ANALYTICS_EVENTS.EXPERT_CALL_BOOKED,
          source: location.pathname,
          calendly_event_type: 'botspot-call',
          experiment_name: 'upgrade_page_book_call_test',
          experiment_variant: 'test',
          user_email: e.data.payload?.email || e.data.invitee?.email,
          call_date: e.data.payload?.event?.start_time || e.data.event?.start_time
        });
        
        // Show success message with countdown
        setShowBookingSuccessRef.current(true);
        
        // Start countdown
        let timeLeft = 3;
        const countdownInterval = setInterval(() => {
          timeLeft--;
          setCountdownRef.current(timeLeft);
          
          if (timeLeft <= 0) {
            clearInterval(countdownInterval);
            // Redirect after countdown completes
            if (fromStarterSignupRef.current) {
              console.log('🗓️ Redirecting to signup survey after call booking');
              navigateRef.current('/signup-survey?call_booked=true&from=pro_consultation');
            } else {
              // For direct visitors, redirect to thank you page or dashboard
              navigateRef.current('/dashboard?call_booked=true');
            }
          }
        }, 1000);
      }
    };

    window.addEventListener('message', handleCalendlyEvent);

    return () => {
      console.log('🔧 [DEBUG] Removing Calendly postMessage event listener...');
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, [location.pathname]); // Always re-attach with location changes

  // Ref for scrolling to Calendly widget
  const calendlyRef = useRef(null);
  
  // Refs to avoid useEffect dependency issues
  const fromStarterSignupRef = useRef(fromStarterSignup);
  const navigateRef = useRef(navigate);
  const setShowBookingSuccessRef = useRef(setShowBookingSuccess);
  const setCountdownRef = useRef(setCountdown);
  
  // Update refs when values change
  useEffect(() => {
    fromStarterSignupRef.current = fromStarterSignup;
    navigateRef.current = navigate;
    setShowBookingSuccessRef.current = setShowBookingSuccess;
    setCountdownRef.current = setCountdown;
  }, [fromStarterSignup, navigate, setShowBookingSuccess, setCountdown]);

  // Handler for primary book call CTA
  const handleBookExpertCall = useCallback((ctaLocation = 'hero') => {
    console.log('🎯 Book Expert Call button clicked!', { ctaLocation });
    
    // 🆕 UNIFIED A/B TEST EVENT: Track primary CTA click for both variants
    pushEvent({
      event: ANALYTICS_EVENTS.UPGRADE_PAGE_PRIMARY_CTA_CLICKED,
      source: location.pathname,
      cta_location: 'hero_book_call_button',
      cta_type: 'book_call',
      experiment_name: 'upgrade_page_book_call_test',
      experiment_variant: 'test',
      button_text: 'Book Free Pro Plan Consultation'
    });

    pushEvent({
      event: ANALYTICS_EVENTS.BOOK_EXPERT_CALL_CLICKED,
      source: location.pathname,
      cta_location: 'hero_book_call_button',
      experiment_name: 'upgrade_page_book_call_test',
      experiment_variant: 'test'
    });

    // Scroll to Calendly widget
    if (calendlyRef.current) {
      calendlyRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center'
      });
    }
  }, [location.pathname]);

  // Handler for secondary direct upgrade CTA
  const handleDirectUpgrade = useCallback((ctaLocation = 'secondary') => {
    console.log('🚀 Direct Upgrade button clicked!', { ctaLocation });
    
    pushEvent({
      event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_CTA_CLICKED,
      source: location.pathname,
      cta_location: ctaLocation,
      cta_type: 'direct_upgrade_secondary',
      button_text: 'Upgrade Directly',
      experiment_name: 'upgrade_page_book_call_test',
      experiment_variant: 'test',
      is_paying_customer: isPayingCustomer
    });

    launchProCheckout(ctaLocation);
  }, [initialEmailForForm, initialNameForForm, isPayingCustomer, launchProCheckout, location.pathname]);

  // Handler for staying on starter plan - FIXED VERSION
  const handleStayOnStarterClick = useCallback(() => {
    console.log('📝 FIXED: Stay on Starter button clicked! Redirecting to signup survey...');
    
    // Fire existing skip upsell event
    pushEvent({
      event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_SKIP_UPSELL_CLICKED, 
      source: location.pathname,
      userEmail: initialEmailForForm,
      experiment_name: 'upgrade_page_book_call_test',
      experiment_variant: 'test'
    });
    
    // Fire generic skip to starter plan event for funnel analytics
    pushEvent({
      event: ANALYTICS_EVENTS.SKIP_TO_STARTER_PLAN,
      source: location.pathname,
      userEmail: initialEmailForForm,
      customerId: customerIdFromQuery,
      upgrade_context: 'pro_upsell_page',
      experiment_name: 'upgrade_page_book_call_test',
      experiment_variant: 'test'
    });
    
    // 🚨 UNIFIED FLOW FIX: Continue to survey instead of dashboard
    // Starter users who skip the Pro upgrade should continue to survey, then onboarding
    const surveyParams = new URLSearchParams();
    if (initialEmailForForm) surveyParams.append('email', initialEmailForForm);
    if (searchParams.get('name')) surveyParams.append('name', searchParams.get('name'));
    if (searchParams.get('customerId')) surveyParams.append('customerId', searchParams.get('customerId'));
    
    // Preserve payment method info if available
    if (searchParams.get('paymentMethodId')) surveyParams.append('paymentMethodId', searchParams.get('paymentMethodId'));
    if (searchParams.get('last4')) surveyParams.append('last4', searchParams.get('last4'));
    
    surveyParams.append('from_starter_signup', 'true');
    surveyParams.append('plan_type', 'starter');
    surveyParams.append('skipped_pro_upgrade', 'true');
    surveyParams.append('source_flow', 'upgrade_to_pro_book_call_skip');
    
    const finalUrl = `/signup-survey?${surveyParams.toString()}`;
    console.log('🚀 NAVIGATING TO:', finalUrl);
    navigate(finalUrl);
  }, [location.pathname, initialEmailForForm, customerIdFromQuery, searchParams, navigate]);

  // Loading state
  if (authLoading || paymentStatusLoading || isLoadingPricing) {
    return (
      <Container className="text-center py-5 upgrade-to-pro-book-call-page">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading Pro Plan Details...</p>
      </Container>
    );
  }

  // Get pricing for display
  const pricing = getFormattedPricing();

  // Pro Plan Features Data (from planFeatures.json)
  const proFeatures = planFeaturesData?.plans?.pro?.features?.filter(f => f.displayInProList)?.map(proFeature => {
    const def = planFeaturesData.featureDefinitions.find(fd => fd.id === proFeature.id);
    if (!def) return null;
    return {
      icon: def.icon === 'FaRobot' ? FaRobot :
            def.icon === 'FaRocket' ? FaRocket :
            def.icon === 'FaBook' ? FaBook :
            def.icon === 'FaPhone' ? FaPhone :
            def.icon === 'FaGraduationCap' ? FaGraduationCap :
            def.icon === 'FaStar' ? FaStar : FaCheckCircle,
      title: def.proListTitle || def.name,
      description: def.proListDescriptionTemplate 
        ? def.proListDescriptionTemplate.replace('{{value}}', proFeature.value) 
        : proFeature.value,
      highlight: proFeature.id === 'ai_bot_generations' ? 'Double the bot building power' :
                 proFeature.id === 'strategy_library' ? '10x more strategies' :
                 proFeature.id === 'bootcamp_access' ? 'Exclusive to Pro members' :
                 proFeature.id === 'priority_1on1_calls' ? 'Direct expert access' :
                 proFeature.id === 'max_active_bots' ? 'More live strategies' :
                 'Pro exclusive feature'
    };
  }).filter(Boolean) || [];

  // Why Book a Call reasons
  const callReasons = [
    {
      icon: FaBrain,
      title: "Personalized Pro Plan Guidance",
      description: "Our experts will show you exactly which Pro features will have the biggest impact for your trading goals and experience level."
    },
    {
      icon: FaChartLine,
      title: "Live Pro AI Bot Builder Demo",
      description: "See the advanced Pro AI features in action and watch us create a custom strategy tailored to your preferred markets."
    },
    {
      icon: FaShieldAlt,
      title: "Smart Investment Decision",
      description: `Pro costs ${pricing.upfront} + ${pricing.monthly} - that's a significant investment. Let's make sure it's the perfect fit for your trading style.`
    }
  ];

  // Success stats
  const successStats = [
    { number: "87%", label: "of call participants upgrade to Pro" },
    { number: "500+", label: "Pro consultations completed" },
    { number: "4.9/5", label: "average expert call rating" }
  ];

  return (
    <div className="upgrade-to-pro-book-call-page">
      <Helmet>
        <title>Book Pro Plan Expert Call - Lumiwealth | Get Personalized Guidance</title>
        <meta name="description" content="Book a free expert consultation to learn how Lumiwealth Pro Plan can transform your trading with AI-powered strategies. Get personalized guidance before investing." />
        <link rel="canonical" href="https://www.lumiwealth.com/upgrade-to-pro-book-call" />
        <script type="text/javascript" src="https://assets.calendly.com/assets/external/widget.js" async />
      </Helmet>


      {/* Hero Section - Much Clearer Pro Plan Focus */}
      <section className="book-call-hero-section">
        <Container>
          <div className="text-center">
            {fromStarterSignup ? (
              <>
                <div className="book-call-badge">
                  <FaCheckCircle className="me-2 text-success" />
                  Starter Account Created — Ready for Pro Plan?
                </div>
                
                <h1 className="book-call-hero-title">
                  Learn About the <span className="highlight">Lumiwealth Pro Plan</span>
                </h1>
                <h2 className="book-call-hero-subtitle">
                  Book a Free Expert Call for Personalized Guidance
                </h2>

                <p className="book-call-hero-description">
                  The Pro Plan gives you 200 AI bot generations, 50+ elite strategies, Live Algo Lab access, 
                  and priority 1-on-1 support. <strong>Investment: {pricing.upfront} + {pricing.monthly}</strong>
                  <br />
                  <strong>Get expert guidance to see if Pro is right for your trading goals.</strong>
                </p>

                {/* Call-focused CTAs - MAIN FOCUS */}
                <div className="book-call-hero-ctas">
                  <Button
                    onClick={() => handleBookExpertCall('hero')}
                    className="book-call-primary-btn"
                    size="lg"
                  >
                    <FaCalendarAlt className="me-2" />
                    Book Free Pro Plan Consultation
                  </Button>
                  
                  <div className="book-call-secondary-option">
                    <p className="book-call-secondary-text">
                      Already know you want Pro Plan?
                    </p>
                    <Button
                      onClick={() => handleDirectUpgrade('hero_secondary')}
                      variant="outline-light"
                      className="book-call-secondary-btn"
                    >
                      <FaRocket className="me-2" />
                      Upgrade to Pro Plan Now
                    </Button>
                  </div>
                </div>

                {/* Continue with Starter - More Prominent at Top */}
                {showStayOnStarterCTAs && (
                  <div className="starter-continue-hero mt-5">
                    <div className="text-center">
                      <p className="mb-3" style={{fontSize: '1.1rem', color: 'rgba(255,255,255,0.8)'}}>
                        Not ready to explore Pro Plan yet?
                      </p>
                      <Button
                        onClick={handleStayOnStarterClick}
                        variant="outline-light"
                        size="lg"
                        className="starter-continue-hero-btn"
                        style={{borderWidth: '2px'}}
                      >
                        Continue with your Starter plan →
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="book-call-badge">
                  <FaRocket className="me-2" />
                  Lumiwealth Pro Plan Information
                </div>
                
                <h1 className="book-call-hero-title">
                  Learn About the <span className="highlight">Lumiwealth Pro Plan</span>
                </h1>
                <h2 className="book-call-hero-subtitle">
                  Book a Free Expert Call for Personalized Guidance
                </h2>

                <p className="book-call-hero-description">
                  The Pro Plan gives you 200 AI bot generations, 50+ elite strategies, Live Algo Lab access, 
                  and priority 1-on-1 support. <strong>Investment: {pricing.upfront} + {pricing.monthly}</strong>
                  <br />
                  <strong>Get expert guidance to see if Pro is right for your trading goals.</strong>
                </p>

                {/* Call-focused CTAs */}
                <div className="book-call-hero-ctas">
                  <Button
                    onClick={() => handleBookExpertCall('hero')}
                    className="book-call-primary-btn"
                    size="lg"
                  >
                    <FaCalendarAlt className="me-2" />
                    Book Free Pro Plan Consultation
                  </Button>
                  
                  <div className="book-call-secondary-option">
                    <p className="book-call-secondary-text">
                      Already know you want Pro Plan?
                    </p>
                    <Button
                      onClick={() => handleDirectUpgrade('hero_secondary')}
                      variant="outline-light"
                      className="book-call-secondary-btn"
                    >
                      <FaRocket className="me-2" />
                      Upgrade to Pro Plan Now
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Success stats */}
            <div className="book-call-hero-stats">
              {successStats.map((stat, index) => (
                <div key={index} className="hero-stat">
                  <div className="hero-stat-number">{stat.number}</div>
                  <div className="hero-stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* What You Get with Pro Plan Section */}
      <section className="book-call-features-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title">
              What's Included in the <span className="highlight">Pro Plan</span>
            </h2>
            <p className="section-subtitle">
              Major upgrades from Starter that justify the investment
            </p>
          </div>

          <Row className="g-4">
            {proFeatures.map((feature, index) => (
              <Col key={index} lg={4} md={6}>
                <Card className="pro-feature-card h-100">
                  <Card.Body className="text-center">
                    <div className="pro-feature-badge">
                      {feature.highlight}
                    </div>
                    <div className="pro-feature-icon">
                      <feature.icon />
                    </div>
                    <h4 className="pro-feature-title">{feature.title}</h4>
                    <p className="pro-feature-description">{feature.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <div className="text-center mt-5">
            <div className="pro-pricing-info">
              <h3 className="pro-pricing-title">Pro Plan Investment</h3>
              <div className="pro-pricing-details">
                <div className="pro-price-main">
                  {pricing.upfront} + {pricing.monthly}
                </div>
                {pricing.isDiscounted && pricing.upfrontOriginal && (
                  <div className="pro-price-original">
                    was {pricing.upfrontOriginal} + {pricing.monthlyOriginal || pricing.monthly}
                  </div>
                )}
                <div className="pro-price-subtitle">
                  Cancel anytime • 30-day money-back guarantee
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Why Book a Call Section */}
      <section className="book-call-reasons-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title">
              Why Book a Pro Plan Expert Call?
            </h2>
            <p className="section-subtitle">
              This is a significant investment. Let's make sure Pro Plan is the right fit for your trading goals.
            </p>
          </div>

          <Row className="g-4">
            {callReasons.map((reason, index) => (
              <Col key={index} lg={4} md={6}>
                <div className="call-reason-card">
                  <div className="call-reason-icon">
                    <reason.icon />
                  </div>
                  <h4 className="call-reason-title">{reason.title}</h4>
                  <p className="call-reason-description">{reason.description}</p>
                </div>
              </Col>
            ))}
          </Row>

          <div className="text-center mt-5">
            <Button
              onClick={() => handleBookExpertCall('reasons')}
              className="book-call-primary-btn"
              size="lg"
            >
              <FaCalendarAlt className="me-2" />
              Book Your Free Pro Plan Call
            </Button>
            <p className="call-commitment-text">
              <strong>15-minute consultation</strong> • No pressure • Expert guidance about Pro Plan
            </p>
          </div>
        </Container>
      </section>

      {/* Calendly Inline Widget Section */}
      <section className="book-call-calendly-section" ref={calendlyRef}>
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title">
              <FaCalendarAlt className="me-3" />
              Schedule Your Pro Plan Consultation
            </h2>
            <p className="section-subtitle">
              Pick a time that works for you. Our Pro Plan experts are standing by to help you decide.
            </p>
          </div>

          {/* Calendly inline widget begin - EXACT user code */}
          <div 
            className="calendly-inline-widget" 
            data-url="https://calendly.com/lumiwealth/botspot-call" 
            style={{minWidth: '320px', height: '700px'}}
          />
          {/* Calendly inline widget end */}
          
          {/* Continue with Starter after calendar section */}
          {fromStarterSignup && (
            <div className="text-center mt-4">
              <p className="mb-2" style={{fontSize: '1rem', color: '#666'}}>
                Not ready to explore Pro Plan yet?
              </p>
              <Button
                onClick={handleStayOnStarterClick}
                variant="outline-primary"
                size="lg"
                className="starter-continue-button"
              >
                Continue with your Starter plan →
              </Button>
            </div>
          )}
        </Container>
      </section>

      {/* Final CTA Section */}
      <section className="book-call-final-cta-section">
        <Container>
          <div className="text-center">
            <h2 className="final-cta-title">
              Ready to Learn About Pro Plan?
            </h2>
            <p className="final-cta-description">
              Get expert guidance about whether Pro Plan is right for your trading goals and budget.
            </p>

            <div className="final-cta-buttons">
              <Button
                className="upgrade-to-pro-book-call-page-final-cta-primary"
                onClick={() => {
                  console.log('📞 Final CTA: Book call button clicked');
                  
                  // 🆕 UNIFIED A/B TEST EVENT: Track primary CTA click for both variants
                  pushEvent({
                    event: ANALYTICS_EVENTS.UPGRADE_PAGE_PRIMARY_CTA_CLICKED,
                    source: location.pathname,
                    cta_location: 'final_cta_book_call',
                    cta_type: 'book_call',
                    experiment_name: 'upgrade_page_book_call_test',
                    experiment_variant: 'test',
                    button_text: 'Book Free Pro Plan Consultation'
                  });

                  pushEvent({
                    event: ANALYTICS_EVENTS.BOOK_EXPERT_CALL_CLICKED,
                    source: location.pathname,
                    cta_location: 'final_cta_book_call',
                    experiment_name: 'upgrade_page_book_call_test',
                    experiment_variant: 'test'
                  });
                  
                  handleBookExpertCall('final_cta_book_call');
                }}
              >
                <FaCalendarAlt className="me-2" />
                Book Pro Plan Consultation
              </Button>
              
              <Button
                onClick={() => handleDirectUpgrade('final')}
                variant="outline-primary"
                className="book-call-primary-btn"
                size="lg"
              >
                <FaRocket className="me-2" />
                Upgrade to Pro Plan
              </Button>
            </div>

            {/* Continue with Starter option - Prominent */}
            {showStayOnStarterCTAs && (
              <div className="final-starter-option mt-5">
                <div className="text-center">
                  <p className="mb-3" style={{fontSize: '1.1rem', color: '#666'}}>
                    Not ready to explore Pro Plan yet?
                  </p>
                  <Button
                    onClick={handleStayOnStarterClick}
                    variant="outline-secondary"
                    size="lg"
                    className="final-starter-button"
                    style={{borderWidth: '2px'}}
                  >
                    Continue with your Starter plan →
                  </Button>
                </div>
              </div>
            )}

            {/* Trust signals */}
            <div className="final-cta-trust">
              <div className="trust-badge">
                <FaShieldAlt className="me-2" />
                30-day money-back guarantee
              </div>
              <div className="trust-badge">
                <FaUsers className="me-2" />
                2,400+ successful Pro members
              </div>
              <div className="trust-badge">
                <FaCheckCircle className="me-2" />
                Cancel anytime
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Booking Success Modal */}
      <InfoModal
        show={showBookingSuccess}
        handleClose={() => {}} // Disable close - auto-redirect will handle it
        title="🎉 Call Booked Successfully!"
        body={
          <>
            <div className="text-center mb-3">
              <div style={{fontSize: '4rem', color: '#28a745'}}>✅</div>
            </div>
            <p className="text-center"><strong>Your Pro Plan consultation has been scheduled!</strong></p>
            <p className="text-center">You'll receive a calendar invitation and email confirmation shortly.</p>
            <p className="text-center text-muted">
              <small>Taking you to the next step in <strong>{countdown}</strong> second{countdown !== 1 ? 's' : ''}...</small>
            </p>
          </>
        }
        actionButton={null} // No action button - auto-redirect
        closeButtonText={null} // No close button - auto-redirect
        showCloseButton={false}
      />

      {/* Exit Intent Modal */}
      <InfoModal
        show={showExitModal}
        handleClose={() => setShowExitModal(false)}
        title="Wait! Before You Go..."
        body={
          <>
            <p>Are you sure you want to leave this exclusive Pro offer?</p>
            <p>The Pro Plan is a significant investment ({pricing.upfront} + {pricing.monthly}). Book a quick, no-pressure 15-minute call with our team. We can show you how Pro Plan features will benefit your specific trading goals.</p>
          </>
        }
        actionButton={{
          text: "Book Free Pro Plan Call",
          onClick: () => {
            pushEvent({ 
              event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_EXIT_INTENT_MODAL_CTA_CLICKED, 
              source: location.pathname,
              variant: 'book_call_page'
            });
            setShowExitModal(false);
            handleBookExpertCall('exit_modal');
          }
        }}
        closeButtonText="No Thanks, I'll Decide Later"
      />
    </div>
  );
}

export default UpgradeToProBookCallPage; 
